/**
 * Eric 2026-08-05 — Page admin « Maintenance des uploads ».
 *
 * Regroupe 2 outils que Vincent nous a demandé après la mise en place du
 * rapport d'upload (bogue 666) et du TTL 24 h sur chunk_data :
 *
 *   1. Purge manuelle chunk_data — bouton pour vider immédiatement la
 *      collection sans attendre le TTL. Utile en cas de rush disque après
 *      un incident. Options : older_than_hours + dry_run.
 *
 *   2. Comparaison des rapports d'envoi — tableau des vidéos triées par
 *      durée d'upload (les plus lentes en tête) pour repérer les vidéos
 *      problématiques (chunks en retry, débit bas, etc.).
 *
 * Founder cross-org / admin scope org courante (backend enforcement).
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { confirmDialog } from "../lib/uiDialogs";
import { useLang } from "../contexts/LanguageContext";
import { Button } from "../components/ui/button";
import {
  Database, Trash, ArrowsClockwise, Warning, Clock, Lightning,
  CheckCircle, SpinnerGap, ArrowLeft, CloudArrowUp, Buildings, CloudSlash,
} from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { API_BASE as API } from "../lib/apiBase";

function formatBytes(bytes) {
  if (!bytes || bytes < 1024) return `${bytes || 0} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} Go`;
}

function formatMs(ms) {
  const s = Math.max(0, Math.round((ms || 0) / 1000));
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return m > 0 ? `${m} min ${sec} s` : `${sec} s`;
}

function formatDate(iso, lang) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(lang === "fr" ? "fr-CA" : "en-CA", {
      day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function UploadMaintenancePage() {
  const { lang } = useLang();
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [purging, setPurging] = useState(false);
  const [olderThan, setOlderThan] = useState(0);

  const [uploadStats, setUploadStats] = useState(null);
  const [uploadStatsLoading, setUploadStatsLoading] = useState(true);
  const [sort, setSort] = useState("slowest");

  // Eric 2026-08-05 bogue 669 v2 — Bulk orphelines
  const [orphans, setOrphans] = useState(null);
  const [orphansLoading, setOrphansLoading] = useState(false);
  const [selectedOrphans, setSelectedOrphans] = useState(new Set());
  const [purgingOrphans, setPurgingOrphans] = useState(false);

  const loadOrphans = useCallback(async () => {
    setOrphansLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/api/admin/videos/orphans?limit=500&include_absent_check=true`,
        { withCredentials: true },
      );
      setOrphans(data);
      setSelectedOrphans(new Set((data.orphans || []).map((v) => v.id)));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossible de lister les orphelines");
    } finally {
      setOrphansLoading(false);
    }
  }, []);

  const toggleOrphan = (id) => {
    setSelectedOrphans((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  };

  const bulkPurge = async (dryRun) => {
    const ids = Array.from(selectedOrphans);
    if (!ids.length) {
      toast.info("Aucune vidéo sélectionnée");
      return;
    }
    if (!dryRun) {
      const ok = await confirmDialog({
        title: `Purger ${ids.length} vidéo(s) orpheline(s) ?`,
        message: `Cela supprimera définitivement ${ids.length} doc(s) vidéo + leurs transcripts/KU associés. Un audit est conservé dans discarded_videos. Le backend re-vérifie que chaque fichier est bien absent avant de supprimer.`,
        confirmLabel: "Purger",
        danger: true,
      });
      if (!ok) return;
    }
    setPurgingOrphans(true);
    try {
      const { data } = await axios.post(
        `${API}/api/admin/videos/orphans/purge`,
        { video_ids: ids, dry_run: !!dryRun },
        { withCredentials: true },
      );
      if (dryRun) {
        toast.info(`Aperçu: ${data.would_delete} seront supprimées, ${data.skipped_present.length} sont en fait présentes (skip).`);
      } else {
        toast.success(`${data.deleted} vidéos purgées. ${data.skipped_present.length} skippées (fichier présent).`);
        setSelectedOrphans(new Set());
        await loadOrphans();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Purge échouée");
    } finally {
      setPurgingOrphans(false);
    }
  };

  // Eric 2026-08-05 (direct upload) — État de la nouvelle section
  const [corsStatus, setCorsStatus] = useState(null);
  const [corsLoading, setCorsLoading] = useState(false);
  const [orgFlags, setOrgFlags] = useState(null);
  const [orgFlagsLoading, setOrgFlagsLoading] = useState(false);
  const [togglingOrgId, setTogglingOrgId] = useState(null);

  const loadCors = useCallback(async () => {
    setCorsLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/videos/upload/direct/cors-check`, { withCredentials: true });
      setCorsStatus(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "CORS check failed");
    } finally {
      setCorsLoading(false);
    }
  }, []);

  const configureCors = async () => {
    const ok = await confirmDialog({
      title: lang === "fr" ? "Configurer CORS sur le bucket ?" : "Configure bucket CORS?",
      message: lang === "fr"
        ? "Cette action ajoute les règles CORS par défaut (knoria.com, preview, localhost). Nécessaire pour que les navigateurs puissent uploader directement vers OVH."
        : "This adds default CORS rules (knoria.com, preview, localhost). Required for browsers to upload directly to OVH.",
      confirmLabel: lang === "fr" ? "Configurer" : "Configure",
    });
    if (!ok) return;
    setCorsLoading(true);
    try {
      const { data } = await axios.post(`${API}/api/videos/upload/direct/cors-configure`, {}, { withCredentials: true });
      setCorsStatus(data);
      toast.success(lang === "fr" ? "CORS configuré." : "CORS configured.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "CORS configure failed");
    } finally {
      setCorsLoading(false);
    }
  };

  const loadOrgFlags = useCallback(async () => {
    setOrgFlagsLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/admin/orgs/direct-upload-flags`, { withCredentials: true });
      setOrgFlags(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Orgs flags load failed");
    } finally {
      setOrgFlagsLoading(false);
    }
  }, []);

  const toggleOrgFlag = async (orgId, enabled) => {
    setTogglingOrgId(orgId);
    try {
      await axios.patch(`${API}/api/admin/orgs/${orgId}/direct-upload`,
        { enabled }, { withCredentials: true });
      await loadOrgFlags();
      toast.success(lang === "fr"
        ? `Direct upload ${enabled ? "activé" : "désactivé"}.`
        : `Direct upload ${enabled ? "enabled" : "disabled"}.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Toggle failed");
    } finally {
      setTogglingOrgId(null);
    }
  };

  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/admin/chunk-data/stats`, { withCredentials: true });
      setStats(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || (lang === "fr" ? "Erreur de chargement des stats" : "Failed to load stats"));
    } finally {
      setStatsLoading(false);
    }
  }, [lang]);

  const loadUploadStats = useCallback(async () => {
    setUploadStatsLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/admin/videos/upload-stats?limit=50&sort=${sort}`, { withCredentials: true });
      setUploadStats(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || (lang === "fr" ? "Erreur de chargement des vidéos" : "Failed to load videos"));
    } finally {
      setUploadStatsLoading(false);
    }
  }, [sort, lang]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  useEffect(() => {
    loadCors();
    loadOrgFlags();
  }, [loadCors, loadOrgFlags]);

  useEffect(() => {
    loadUploadStats();
  }, [loadUploadStats]);

  const handlePurge = async () => {
    const scope = olderThan > 0
      ? (lang === "fr" ? `plus vieux que ${olderThan} h` : `older than ${olderThan} h`)
      : (lang === "fr" ? "TOUS les chunks" : "ALL chunks");
    const ok = await confirmDialog({
      title: lang === "fr" ? "Purger chunk_data ?" : "Purge chunk_data?",
      message: lang === "fr"
        ? `Cela supprimera ${scope}. Les uploads EN COURS sur ce pod ne sont pas interrompus (le fichier disque reste), mais si un chunk avait été routé vers un autre pod, l'utilisateur devra relancer. Continuer ?`
        : `This will delete ${scope}. In-progress uploads on this pod are not interrupted (local disk file remains), but if a chunk had been routed to another pod, the user will need to restart. Continue?`,
      confirmLabel: lang === "fr" ? "Purger maintenant" : "Purge now",
      danger: true,
    });
    if (!ok) return;
    setPurging(true);
    try {
      const params = new URLSearchParams();
      if (olderThan > 0) params.append("older_than_hours", String(olderThan));
      const { data } = await axios.post(
        `${API}/api/admin/chunk-data/purge?${params.toString()}`,
        {},
        { withCredentials: true },
      );
      toast.success(
        lang === "fr"
          ? `${data.deleted} chunks supprimés (sur ${data.matched} correspondants).`
          : `${data.deleted} chunks deleted (of ${data.matched} matched).`,
      );
      await loadStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || (lang === "fr" ? "Erreur de purge" : "Purge failed"));
    } finally {
      setPurging(false);
    }
  };

  const handleDryRun = async () => {
    try {
      const params = new URLSearchParams({ dry_run: "true" });
      if (olderThan > 0) params.append("older_than_hours", String(olderThan));
      const { data } = await axios.post(
        `${API}/api/admin/chunk-data/purge?${params.toString()}`,
        {},
        { withCredentials: true },
      );
      toast.info(
        lang === "fr"
          ? `${data.matched} chunks seraient supprimés (aperçu, aucun changement).`
          : `${data.matched} chunks would be deleted (dry-run, no changes).`,
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  return (
    <div className="max-w-6xl mx-auto animate-fade-in" data-testid="upload-maintenance-page">
      <div className="mb-6">
        <Link to="/dashboard" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-2">
          <ArrowLeft size={12} /> {lang === "fr" ? "Retour au tableau de bord" : "Back to dashboard"}
        </Link>
        <h1 className="text-2xl font-bold tracking-tight font-['Outfit']">
          {lang === "fr" ? "Maintenance des uploads" : "Upload maintenance"}
        </h1>
        <p className="text-muted-foreground text-sm mt-1">
          {lang === "fr"
            ? "Purge manuelle des chunks abandonnés et analyse des durées d'envoi par vidéo."
            : "Manual purge of abandoned chunks and per-video upload duration analysis."}
        </p>
      </div>

      {/* ── SECTION 0 : Direct upload (browser → OVH S3) ── */}
      <section className="mb-8 bg-card border border-border rounded-xl p-5" data-testid="direct-upload-section">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <CloudArrowUp size={18} className="text-cyan-500" />
            {lang === "fr" ? "Direct upload (navigateur → OVH S3)" : "Direct upload (browser → OVH S3)"}
          </h2>
          <Button variant="outline" size="sm" onClick={() => { loadCors(); loadOrgFlags(); }} data-testid="direct-refresh-btn">
            <ArrowsClockwise size={14} className={(corsLoading || orgFlagsLoading) ? "animate-spin" : ""} />
            {lang === "fr" ? "Rafraîchir" : "Refresh"}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mb-4">
          {lang === "fr"
            ? "Nouveau flow d'upload : le navigateur envoie les parts directement à OVH via URLs présignées. Le backend ne voit jamais les bytes du fichier. Prérequis : CORS configuré sur le bucket + flag activé sur l'org."
            : "New upload flow: browser sends parts directly to OVH via presigned URLs. Backend never sees file bytes. Requires: bucket CORS configured + flag enabled per org."}
        </p>

        {/* CORS */}
        <div className="p-3 border border-border rounded-lg mb-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold">
                {lang === "fr" ? "CORS sur le bucket OVH" : "OVH bucket CORS"}
              </p>
              <p className="text-xs text-muted-foreground mt-0.5" data-testid="cors-status">
                {corsLoading
                  ? (lang === "fr" ? "Vérification…" : "Checking…")
                  : corsStatus?.status === "configured"
                  ? (lang === "fr" ? `Configuré (${corsStatus.rules?.length || 0} règle(s))` : `Configured (${corsStatus.rules?.length || 0} rule(s))`)
                  : (lang === "fr" ? "Non configuré — les uploads directs échoueront" : "Not configured — direct uploads will fail")}
              </p>
            </div>
            <Button
              variant={corsStatus?.status === "configured" ? "outline" : "default"}
              size="sm"
              onClick={configureCors}
              disabled={corsLoading}
              data-testid="cors-configure-btn"
            >
              {corsStatus?.status === "configured"
                ? (lang === "fr" ? "Reconfigurer" : "Reconfigure")
                : (lang === "fr" ? "Configurer" : "Configure")}
            </Button>
          </div>
        </div>

        {/* Org flags */}
        <div className="p-3 border border-border rounded-lg">
          <p className="text-sm font-semibold mb-2 flex items-center gap-2">
            <Buildings size={14} />
            {lang === "fr" ? "Activation par organisation" : "Per-organization activation"}
          </p>
          {orgFlagsLoading ? (
            <div className="text-xs text-muted-foreground flex items-center gap-2">
              <SpinnerGap size={12} className="animate-spin" />
              {lang === "fr" ? "Chargement…" : "Loading…"}
            </div>
          ) : orgFlags?.orgs ? (
            <div className="space-y-2" data-testid="org-flags-list">
              {orgFlags.orgs.map((o) => (
                <div
                  key={o.id}
                  className="flex items-center justify-between text-xs py-1.5 border-b border-border/40 last:border-0"
                  data-testid={`org-flag-row-${o.id}`}
                >
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">{o.name || o.id}</span>
                    <span className="text-[10px] text-muted-foreground">
                      {o.storage_region || "default"}
                    </span>
                    {!o.s3_eligible && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                        {lang === "fr" ? "Non-Loi 25" : "Non-Loi25"}
                      </span>
                    )}
                  </div>
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <span className={`text-[10px] ${o.direct_upload_enabled ? "text-teal-700 dark:text-teal-300 font-semibold" : "text-muted-foreground"}`}>
                      {o.direct_upload_enabled
                        ? (lang === "fr" ? "Activé" : "Enabled")
                        : (lang === "fr" ? "Désactivé" : "Disabled")}
                    </span>
                    <input
                      type="checkbox"
                      checked={!!o.direct_upload_enabled}
                      disabled={!o.s3_eligible || togglingOrgId === o.id}
                      onChange={(e) => toggleOrgFlag(o.id, e.target.checked)}
                      data-testid={`org-flag-toggle-${o.id}`}
                    />
                  </label>
                </div>
              ))}
            </div>
          ) : null}
          {!orgFlags?.s3_available && (
            <p className="text-xs text-amber-600 mt-2">
              {lang === "fr"
                ? "S3 non disponible côté backend (env vars S3_CANADA_* manquantes)."
                : "S3 not available on backend (missing S3_CANADA_* env vars)."}
            </p>
          )}
        </div>
      </section>

      {/* ── SECTION 1 : chunk_data purge ── */}
      <section className="mb-8 bg-card border border-border rounded-xl p-5" data-testid="chunk-data-section">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Database size={18} className="text-cyan-500" />
            {lang === "fr" ? "Collection chunk_data" : "chunk_data collection"}
          </h2>
          <Button variant="outline" size="sm" onClick={loadStats} disabled={statsLoading} data-testid="chunk-stats-refresh-btn">
            <ArrowsClockwise size={14} className={statsLoading ? "animate-spin" : ""} />
            {lang === "fr" ? "Rafraîchir" : "Refresh"}
          </Button>
        </div>

        {statsLoading ? (
          <div className="flex items-center gap-2 text-muted-foreground text-sm">
            <SpinnerGap size={16} className="animate-spin" />
            {lang === "fr" ? "Chargement…" : "Loading…"}
          </div>
        ) : stats && (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-5">
              <div className="flex flex-col">
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                  {lang === "fr" ? "Chunks en DB" : "Chunks in DB"}
                </span>
                <span className="text-2xl font-bold" data-testid="stat-total-chunks">{stats.total_chunks || 0}</span>
              </div>
              <div className="flex flex-col">
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                  {lang === "fr" ? "Espace utilisé" : "Space used"}
                </span>
                <span className="text-2xl font-bold" data-testid="stat-total-bytes">{formatBytes(stats.total_bytes)}</span>
              </div>
              <div className="flex flex-col">
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                  {lang === "fr" ? "Uploads distincts" : "Distinct uploads"}
                </span>
                <span className="text-2xl font-bold">{stats.distinct_upload_ids || 0}</span>
              </div>
              <div className="flex flex-col">
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                  {lang === "fr" ? "Plus vieux" : "Oldest"}
                </span>
                <span className="text-sm font-semibold">{formatDate(stats.oldest_created_at, lang)}</span>
              </div>
            </div>

            <div className="p-4 bg-amber-500/5 border border-amber-500/30 rounded-lg">
              <div className="flex items-start gap-2 mb-3">
                <Warning size={16} className="text-amber-500 flex-shrink-0 mt-0.5" weight="fill" />
                <div className="text-xs text-muted-foreground">
                  {lang === "fr"
                    ? "Le TTL 24 h nettoie déjà automatiquement en arrière-plan. Utilisez cette purge manuelle uniquement en cas d'urgence (rush disque, après un incident)."
                    : "The 24h TTL cleans up automatically in the background. Use this manual purge only in emergencies (disk pressure, post-incident)."}
                </div>
              </div>
              <div className="flex items-end gap-3 flex-wrap">
                <div className="flex flex-col">
                  <label className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
                    {lang === "fr" ? "Purger les chunks plus vieux que" : "Purge chunks older than"}
                  </label>
                  <select
                    value={olderThan}
                    onChange={(e) => setOlderThan(parseInt(e.target.value, 10))}
                    className="text-sm border border-border rounded-md px-2 py-1.5 bg-background"
                    data-testid="chunk-purge-scope-select"
                  >
                    <option value={0}>{lang === "fr" ? "Tout (0 h)" : "All (0 h)"}</option>
                    <option value={1}>1 h</option>
                    <option value={6}>6 h</option>
                    <option value={12}>12 h</option>
                    <option value={24}>24 h</option>
                    <option value={72}>72 h</option>
                  </select>
                </div>
                <Button variant="outline" size="sm" onClick={handleDryRun} data-testid="chunk-purge-dry-run-btn">
                  <CheckCircle size={14} />
                  {lang === "fr" ? "Aperçu (dry-run)" : "Preview (dry-run)"}
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handlePurge}
                  disabled={purging || (stats.total_chunks || 0) === 0}
                  data-testid="chunk-purge-btn"
                >
                  {purging ? <SpinnerGap size={14} className="animate-spin" /> : <Trash size={14} />}
                  {lang === "fr" ? "Purger maintenant" : "Purge now"}
                </Button>
              </div>
            </div>
          </>
        )}
      </section>

      {/* ── SECTION 1.5 : Vidéos orphelines (fichier absent d'OVH) ── */}
      <section className="mb-8 bg-card border border-border rounded-xl p-5" data-testid="orphans-section">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <CloudSlash size={18} className="text-red-500" />
            {lang === "fr" ? "Vidéos orphelines (fichier absent d'OVH)" : "Orphan videos (file missing from OVH)"}
          </h2>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={loadOrphans} disabled={orphansLoading} data-testid="orphans-refresh-btn">
              <ArrowsClockwise size={14} className={orphansLoading ? "animate-spin" : ""} />
              {lang === "fr" ? "Détecter" : "Detect"}
            </Button>
          </div>
        </div>
        <p className="text-xs text-muted-foreground mb-4">
          {lang === "fr"
            ? "Vérifie chaque vidéo `failed` via head_object OVH. Les vraies orphelines (fichier absent) peuvent être purgées en masse — un audit est conservé dans discarded_videos."
            : "Checks each `failed` video via OVH head_object. Real orphans (file absent) can be bulk-purged — audit trail kept in discarded_videos."}
        </p>

        {orphans === null ? (
          <p className="text-xs text-muted-foreground">
            {lang === "fr" ? "Clique « Détecter » pour scanner." : "Click « Detect » to scan."}
          </p>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="flex flex-col p-3 bg-muted rounded-lg">
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                  {lang === "fr" ? "Total failed" : "Total failed"}
                </span>
                <span className="text-xl font-bold">{orphans.total_failed || 0}</span>
              </div>
              <div className="flex flex-col p-3 bg-red-500/10 rounded-lg">
                <span className="text-[10px] uppercase tracking-wider text-red-700 dark:text-red-300">
                  {lang === "fr" ? "Orphelines (fichier absent)" : "Orphans (file absent)"}
                </span>
                <span className="text-xl font-bold text-red-700 dark:text-red-300" data-testid="orphans-count">
                  {orphans.orphans_count || 0}
                </span>
              </div>
              <div className="flex flex-col p-3 bg-amber-500/10 rounded-lg">
                <span className="text-[10px] uppercase tracking-wider text-amber-700 dark:text-amber-300">
                  {lang === "fr" ? "Failed mais fichier présent" : "Failed but file present"}
                </span>
                <span className="text-xl font-bold text-amber-700 dark:text-amber-300">
                  {orphans.false_positives?.length || 0}
                </span>
              </div>
            </div>

            {(orphans.orphans || []).length > 0 && (
              <>
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <Button variant="outline" size="sm" onClick={() => bulkPurge(true)} disabled={purgingOrphans || selectedOrphans.size === 0} data-testid="orphans-dry-run-btn">
                    <CheckCircle size={14} />
                    {lang === "fr" ? `Aperçu (${selectedOrphans.size})` : `Preview (${selectedOrphans.size})`}
                  </Button>
                  <Button variant="destructive" size="sm" onClick={() => bulkPurge(false)} disabled={purgingOrphans || selectedOrphans.size === 0} data-testid="orphans-purge-btn">
                    {purgingOrphans ? <SpinnerGap size={14} className="animate-spin" /> : <Trash size={14} />}
                    {lang === "fr" ? `Purger ${selectedOrphans.size} orpheline(s)` : `Purge ${selectedOrphans.size} orphan(s)`}
                  </Button>
                  <span className="text-[10px] text-muted-foreground">
                    {lang === "fr" ? "Le backend recheck chaque fichier avant suppression." : "Backend re-checks each file before deletion."}
                  </span>
                </div>
                <div className="overflow-x-auto max-h-96 border border-border rounded-lg">
                  <table className="w-full text-sm">
                    <thead className="text-xs uppercase tracking-wider text-muted-foreground bg-muted sticky top-0">
                      <tr>
                        <th className="text-left py-2 px-2 w-8">
                          <input
                            type="checkbox"
                            checked={selectedOrphans.size === orphans.orphans.length}
                            onChange={(e) => setSelectedOrphans(new Set(e.target.checked ? orphans.orphans.map((v) => v.id) : []))}
                            data-testid="orphans-select-all"
                          />
                        </th>
                        <th className="text-left py-2 px-2">{lang === "fr" ? "Titre" : "Title"}</th>
                        <th className="text-left py-2 px-2">{lang === "fr" ? "Erreur" : "Error"}</th>
                        <th className="text-left py-2 px-2">{lang === "fr" ? "Stage" : "Stage"}</th>
                        <th className="text-left py-2 px-2">{lang === "fr" ? "Créée" : "Created"}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orphans.orphans.map((v) => (
                        <tr key={v.id} className="border-t border-border/40 hover:bg-secondary/50" data-testid={`orphan-row-${v.id}`}>
                          <td className="py-1.5 px-2">
                            <input
                              type="checkbox"
                              checked={selectedOrphans.has(v.id)}
                              onChange={() => toggleOrphan(v.id)}
                              data-testid={`orphan-checkbox-${v.id}`}
                            />
                          </td>
                          <td className="py-1.5 px-2 max-w-[240px] truncate" title={v.title}>{v.title || v.id.slice(0, 8)}</td>
                          <td className="py-1.5 px-2 text-xs text-muted-foreground max-w-[280px] truncate" title={v.error}>{v.error || "—"}</td>
                          <td className="py-1.5 px-2 text-xs">{v.pipeline_stage || "—"}</td>
                          <td className="py-1.5 px-2 text-xs text-muted-foreground">{formatDate(v.created_at, lang)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
            {(orphans.orphans || []).length === 0 && orphans.total_failed > 0 && (
              <p className="text-xs text-teal-600 dark:text-teal-400">
                {lang === "fr"
                  ? `Aucune orpheline détectée sur ${orphans.total_failed} vidéo(s) failed. Les échecs viennent d'autres causes (ffmpeg, transcription, etc.).`
                  : `No orphans detected on ${orphans.total_failed} failed video(s). Failures are from other causes (ffmpeg, transcription, etc.).`}
              </p>
            )}
          </>
        )}
      </section>

      {/* ── SECTION 2 : Upload stats ── */}
      <section className="bg-card border border-border rounded-xl p-5" data-testid="upload-stats-section">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Clock size={18} className="text-cyan-500" />
            {lang === "fr" ? "Rapports d'envoi par vidéo" : "Per-video upload reports"}
          </h2>
          <div className="flex items-center gap-2">
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="text-sm border border-border rounded-md px-2 py-1.5 bg-background"
              data-testid="upload-stats-sort-select"
            >
              <option value="slowest">{lang === "fr" ? "Les plus lentes" : "Slowest first"}</option>
              <option value="fastest">{lang === "fr" ? "Les plus rapides" : "Fastest first"}</option>
              <option value="most_retries">{lang === "fr" ? "Le plus de retries" : "Most retries"}</option>
              <option value="newest">{lang === "fr" ? "Plus récentes" : "Newest"}</option>
            </select>
            <Button variant="outline" size="sm" onClick={loadUploadStats} disabled={uploadStatsLoading} data-testid="upload-stats-refresh-btn">
              <ArrowsClockwise size={14} className={uploadStatsLoading ? "animate-spin" : ""} />
              {lang === "fr" ? "Rafraîchir" : "Refresh"}
            </Button>
          </div>
        </div>

        {uploadStatsLoading ? (
          <div className="flex items-center gap-2 text-muted-foreground text-sm">
            <SpinnerGap size={16} className="animate-spin" />
            {lang === "fr" ? "Chargement…" : "Loading…"}
          </div>
        ) : uploadStats && uploadStats.videos.length === 0 ? (
          <div className="text-center py-10 text-sm text-muted-foreground">
            <Lightning size={24} className="mx-auto mb-2 text-muted-foreground/40" />
            {lang === "fr"
              ? "Aucune vidéo n'a encore de rapport d'envoi. Les rapports sont ajoutés automatiquement à chaque nouveau upload depuis le bogue 666."
              : "No videos have upload reports yet. Reports are added automatically on each new upload since bug 666."}
          </div>
        ) : uploadStats && (
          <>
            <div className="text-xs text-muted-foreground mb-3">
              {lang === "fr"
                ? `${uploadStats.returned} vidéo(s) affichée(s) sur ${uploadStats.total_with_report} avec rapport`
                : `Showing ${uploadStats.returned} of ${uploadStats.total_with_report} videos with report`}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="upload-stats-table">
                <thead className="text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                  <tr>
                    <th className="text-left py-2 px-2">{lang === "fr" ? "Titre" : "Title"}</th>
                    <th className="text-right py-2 px-2">{lang === "fr" ? "Durée" : "Duration"}</th>
                    <th className="text-right py-2 px-2">{lang === "fr" ? "Débit" : "Throughput"}</th>
                    <th className="text-right py-2 px-2">{lang === "fr" ? "Taille" : "Size"}</th>
                    <th className="text-right py-2 px-2">{lang === "fr" ? "Retries" : "Retries"}</th>
                    <th className="text-left py-2 px-2">{lang === "fr" ? "Mode" : "Mode"}</th>
                    <th className="text-left py-2 px-2">{lang === "fr" ? "Enregistré" : "Saved"}</th>
                  </tr>
                </thead>
                <tbody>
                  {uploadStats.videos.map((v) => {
                    const r = v.upload_report || {};
                    const totalSec = Math.max(0, Math.round((r.total_ms || 0) / 1000));
                    const mo = (r.file_size || 0) / (1024 * 1024);
                    const throughput = totalSec > 0 ? (mo / totalSec).toFixed(1) : "—";
                    const hasRetries = (r.total_retries || 0) > 0;
                    return (
                      <tr
                        key={v.id}
                        className={`border-b border-border/50 hover:bg-secondary/50 ${hasRetries ? "bg-amber-500/5" : ""}`}
                        data-testid={`upload-stats-row-${v.id}`}
                      >
                        <td className="py-2 px-2">
                          <Link to={`/dashboard/video/${v.id}`} className="text-cyan-600 hover:underline" data-testid={`upload-stats-link-${v.id}`}>
                            {v.title || v.id.slice(0, 8)}
                          </Link>
                        </td>
                        <td className="text-right py-2 px-2 font-mono">{formatMs(r.total_ms)}</td>
                        <td className="text-right py-2 px-2 font-mono">{throughput} Mo/s</td>
                        <td className="text-right py-2 px-2 font-mono">{formatBytes(r.file_size)}</td>
                        <td className={`text-right py-2 px-2 font-mono ${hasRetries ? "text-amber-700 dark:text-amber-300 font-semibold" : ""}`}>
                          {r.total_retries || 0}
                          {hasRetries && r.chunks_with_retries ? (
                            <span className="text-[10px] text-muted-foreground ml-1">
                              ({r.chunks_with_retries}/{r.total_chunks})
                            </span>
                          ) : null}
                        </td>
                        <td className="py-2 px-2 text-xs">
                          {r.mode === "chunked" ? `Chunké · ${r.total_chunks || 0}` : "Direct"}
                          {r.resumed && <span className="ml-1 text-[10px] text-amber-600">↻</span>}
                        </td>
                        <td className="py-2 px-2 text-xs text-muted-foreground">{formatDate(r.saved_at, lang)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </section>
    </div>
  );
}
